import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateRange } from 'react-day-picker'

import { getSpeedOfServiceMetrics } from '@/api/clarity'
import {
  MetricCardComparisonModel,
  SpeedOfServiceMetricsModel,
} from '@/types/clarity'
import { dateToString } from '@/utils'
import { DateType, getDateRange, getTrendingDate } from '@/utils/date'

const COMPANY_SYSTEM = 'Company System'

export default function useSpeedMetrics(
  startDate: string,
  endDate: string,
  dateFilterType: DateType,
  branches: string
) {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [branchData, setBranchData] = useState<SpeedOfServiceMetricsModel[]>([])
  const [branchTrendData, setBranchTrendData] = useState<
    SpeedOfServiceMetricsModel[]
  >([])
  const [companyData, setCompanyData] = useState<SpeedOfServiceMetricsModel>()
  const [companyTrendData, setCompanyTrendData] =
    useState<SpeedOfServiceMetricsModel>()
  const [metricCardData, setMetricCardData] =
    useState<MetricCardComparisonModel<SpeedOfServiceMetricsModel>>()

  const fetchData = useMemo(() => {
    return async () => {
      try {
        const { branchMetrics, companyMetrics } = await fetchSpeedMetrics(
          branches,
          startDate,
          endDate
        )
        setLocalTrendingData(branches, setMetricCardData)

        setBranchData(branchMetrics)
        setCompanyData(companyMetrics)
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      }
    }
  }, [branches.toString(), startDate, endDate])

  const fetchTrendData = useMemo(() => {
    return async () => {
      try {
        const trendingDate = getTrendingDate(
          { from: new Date(startDate), to: new Date(endDate) },
          dateFilterType
        ) as DateRange

        const { branchMetrics, companyMetrics } = await fetchSpeedMetrics(
          branches,
          dateToString(trendingDate, 'from'),
          dateToString(trendingDate, 'to')
        )

        setBranchTrendData(branchMetrics)
        setCompanyTrendData(companyMetrics)
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      }
    }
  }, [branches.toString(), startDate, endDate, dateFilterType])

  const fetchAllData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      await Promise.all([fetchData(), fetchTrendData()])
    } catch (err) {
      setError(
        new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )
      )
    } finally {
      setLoading(false)
    }
  }, [fetchData, fetchTrendData])

  const refetch = useCallback(() => {
    console.log('Refetching speed metrics data...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    branchData,
    companyData,
    branchTrendData,
    companyTrendData,
    metricCardData,
    loading,
    error,
    refetch,
  }
}

async function setLocalTrendingData(
  branches: string,
  setMetricCardData: (
    data: MetricCardComparisonModel<SpeedOfServiceMetricsModel>
  ) => void
) {
  const mtdDate = getDateRange('month-to-date')
  const ytdDate = getDateRange('year-to-date')
  const mtdTrendingDate = mtdDate.trending as DateRange
  const ytdTrendingDate = ytdDate.trending as DateRange

  const { companyMetrics: mCompanyMetrics } = await fetchSpeedMetrics(
    branches,
    dateToString(mtdDate, 'from'),
    dateToString(mtdDate, 'to')
  )

  const { companyMetrics: mCompanyMetricsTrending } = await fetchSpeedMetrics(
    branches,
    dateToString(mtdTrendingDate, 'from'),
    dateToString(mtdTrendingDate, 'to')
  )

  const { companyMetrics: yCompanyMetrics } = await fetchSpeedMetrics(
    branches,
    dateToString(ytdDate, 'from'),
    dateToString(ytdDate, 'to')
  )

  const { companyMetrics: yCompanyMetricsTrending } = await fetchSpeedMetrics(
    branches,
    dateToString(ytdTrendingDate, 'from'),
    dateToString(ytdTrendingDate, 'to')
  )

  setMetricCardData({
    currMetricsMonthly: mCompanyMetrics,
    currMetricsYearly: yCompanyMetrics,
    prevMetricsMonthly: mCompanyMetricsTrending,
    prevMetricsYearly: yCompanyMetricsTrending,
  })
}

async function fetchSpeedMetrics(
  branches: string,
  startDate: string,
  endDate: string
): Promise<{
  branchMetrics: SpeedOfServiceMetricsModel[]
  companyMetrics: SpeedOfServiceMetricsModel
}> {
  try {
    const response = await getSpeedOfServiceMetrics(
      branches,
      startDate,
      endDate
    )

    const metrics: SpeedOfServiceMetricsModel[] = response.map(
      (responseMetrics) =>
        setMetric(
          responseMetrics.stand,
          responseMetrics.windowEarlyAM,
          responseMetrics.windowAM,
          responseMetrics.windowMid,
          responseMetrics.windowPM,
          responseMetrics.windowTotal,
          responseMetrics.windowNonAdjTotal,
          responseMetrics.serviceEarlyAM,
          responseMetrics.serviceAM,
          responseMetrics.serviceMid,
          responseMetrics.servicePM,
          responseMetrics.serviceTotal,
          responseMetrics.serviceNonAdjTotal,
          responseMetrics.speedOfService,
          responseMetrics.utilization,
          responseMetrics.utilizationByShift
            ? responseMetrics.utilizationByShift
            : [[0, 0, 0, 0]]
        )
    )

    const branchMetrics = metrics.filter(
      (metric) => metric.stand !== COMPANY_SYSTEM
    )
    const companyMetrics = metrics.filter(
      (metric) => metric.stand === COMPANY_SYSTEM
    )

    return {
      branchMetrics,
      companyMetrics: companyMetrics[0],
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function setMetric(
  stand: string,
  windowEarlyAM: number,
  windowAM: number,
  windowMid: number,
  windowPM: number,
  windowTotal: number,
  windowNonAdjTotal: number,
  serviceEarlyAM: number,
  serviceAM: number,
  serviceMid: number,
  servicePM: number,
  serviceTotal: number,
  serviceNonAdjTotal: number,
  speedOfService: number,
  utilization: number,
  utilizationByShift: number[][]
): SpeedOfServiceMetricsModel {
  return {
    stand: stand === 'system_total' ? COMPANY_SYSTEM : stand,
    windowEarlyAM,
    windowAM,
    windowMid,
    windowPM,
    windowTotal,
    windowNonAdjTotal,
    serviceEarlyAM,
    serviceAM,
    serviceMid,
    servicePM,
    serviceTotal,
    serviceNonAdjTotal,
    speedOfService,
    utilization,
    utilizationByShift,
  }
}
